import React, { useRef } from 'react';
import { PRINT_ADS } from '../config';
import { fmtDollars } from '../utils/pricing';

// Prices in the on-screen copy come from config.js so the "edit config.js to
// update prices" maintenance promise keeps holding for this step too.
const FULL_PAGE = PRINT_ADS.find(a => a.id === 'full_page');
const MIN_WEEKLY = Math.min(...PRINT_ADS.map(a => a.price));

const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

export default function Step1OrgType({ form, onChangeOrgType, onNext }) {
  const cardRefs = useRef({});
  const isNonprofit = form.orgType === 'nonprofit';

  const options = [
    {
      type: 'business',
      label: 'A local business',
      // The pill only renders while business is the active selection, so the
      // unselected card never carries a competing accent-colored cue.
      badge: selected => selected && <span className="option-pill">Pre-selected</span>,
      spec: 'Shops, restaurants, services, professionals, and political ad campaigns.',
      footnote: null,
    },
    {
      type: 'nonprofit',
      label: 'A non-profit or community group',
      badge: () => <span className="nonprofit-rate-badge">Non-profit discount</span>,
      spec: 'Discounted rates for verified 501(c)(3) organizations.',
      footnote: "No EIN needed now — we'll ask with your contact info.",
    },
  ];

  function handleKeyDown(e, type) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChangeOrgType(type, 'step1');
    } else if (ARROW_KEYS.includes(e.key)) {
      e.preventDefault();
      const other = type === 'business' ? 'nonprofit' : 'business';
      onChangeOrgType(other, 'step1');
      if (cardRefs.current[other]) cardRefs.current[other].focus();
    }
  }

  return (
    <div className="step-card">
      <h2 className="step-heading" id="org-type-heading">Welcome &mdash; let's get you the right rates.</h2>
      <p className="step-subheading">
        Ads start at {fmtDollars(MIN_WEEKLY)} a week. Standard business rates are shown
        by default &mdash; switch below, and every price updates instantly.
      </p>

      <div role="radiogroup" aria-labelledby="org-type-heading">
        {options.map(opt => {
          const selected = form.orgType === opt.type;
          return (
            <div
              key={opt.type}
              ref={el => { cardRefs.current[opt.type] = el; }}
              className={`radio-option${selected ? ' selected' : ''}`}
              onClick={() => onChangeOrgType(opt.type, 'step1')}
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onKeyDown={e => handleKeyDown(e, opt.type)}
            >
              <input type="radio" readOnly checked={selected} tabIndex={-1} aria-hidden="true" />
              <div className="option-info">
                <div className="option-label">
                  {opt.label}
                  {opt.badge(selected)}
                </div>
                <div className="option-spec">{opt.spec}</div>
                {opt.footnote && <div className="option-footnote">{opt.footnote}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Savings confirmation — dollar figures are only revealed after someone
          self-identifies as a non-profit, so business customers never see them. */}
      <div aria-live="polite">
        {isNonprofit && (
          <div className="nonprofit-note">
            &#10003; Non-profit rates applied &mdash; for example, a full page runs{' '}
            {fmtDollars(FULL_PAGE.nonprofitPrice)} instead of {fmtDollars(FULL_PAGE.price)}.
            We'll confirm your 501(c)(3) EIN with your contact info.
          </div>
        )}
      </div>

      <div className="nav-buttons step1-nav">
        <span className="trust-line">
          No payment until the final step &mdash; you'll see your exact total before you pay a cent.
        </span>
        <button className="btn btn-primary" onClick={onNext}>
          See ad sizes &amp; prices &rarr;
        </button>
      </div>
    </div>
  );
}
